<template>
    <div class="data-status-grid">
        <loading-spinner :show-loading="showLoading"></loading-spinner>
        <information-modal :title="selectedErrorCode" 
            :show-modal="showErrorDescriptionModal" @dismiss="handleErrorDescriptionModalDismiss">
            <template #body>
                <div v-html="selectedErrorBody">                    
                </div>
            </template>
        </information-modal>
        <information-modal :title="updateModalTitle"
            :show-modal="showUpdateModal"
            :show-footer="true"
            :modal-style="'max-width:950px'"
            @dismiss="handleUpdateModalDismiss">
            <template #body="{ onDismiss }">
                <errors-warnings-update :item="selectedItem"
                    :url="`${url}/update/`"
                    :dataset="dataset"
                    @dismiss="onDismiss"></errors-warnings-update>
            </template>
        </information-modal>
        <b-filter-modal :isShowFilterEditor="isShowFilterEditor"
            :filterOptions="filterOptions"
            :filterableFields="filterableFields"
            @filterChanged="applyFilters"
            @dismissModal="handleDismiss"></b-filter-modal>
        <b-table-header :value="options"
            @optionChange="handleOptionChange"
            header-slot-class="b-header-slot w-100 position-relative"
            :filters="filterOptions.filters"
            :current-item-count="numberOfReturnedItems">
            <template #default>                
                <button @click="showFilterEditor($event)"
                    class="btn btn-primary">
                    <i class="bi bi-search pe-1"></i>
                    Filter
                </button>
                <slot name="header.toolbar"></slot>
                <span class="filter-description"
                    v-if="hasAppliedFilters">
                    {{ filtersDescription }}
                </span>
                <button v-if="hasAppliedFilters"
                    class="btn-close "
                    v-tooltip="'Remove all currently applied filters.'"
                    @click="resetFilters()">
                </button>
            </template> 
            <template #pagingStart>
                <div class="btn-group" role="group">
                    <drop-down label="Export Data" :item-count="2" :is-primary-button="true">
                        <template v-slot:item1="{ item }">
                            <a class="dropdown-item"
                                href="javascript:void"
                                @click.prevent="exportData('csv')">Download
                                as .csv</a>
                        </template>
                        <template v-slot:item2="{ item }">
                            <a class="dropdown-item"
                                href="javascript:void"
                                @click.prevent="exportData('xlsx')">Download
                                as .xlsx</a>
                        </template>
                    </drop-down>
                </div>
            </template>           
            <template #headerTopBar>
                <div class="form-check d-inline-block">
                    <input id="cbAddCol"
                        type="checkbox"
                        class="form-check-input"
                        v-model="showAdditionalColumns" />
                    <label for="cbAddCol"
                        class="form-check-label">Display with the latest explanation/justification for
                        Status
                        change</label>
                </div>
                <div class="btn-group ms-auto"
                    style=""
                    role="group">
                    <drop-down label="Error/Warning Details" :item-count="2" :is-primary-button="false">
                        <template v-slot:item1="{}">
                            <a class="dropdown-item" :href="`${store.errorDescriptionsDownloadUrl}csv`">Download as .csv</a>
                        </template>
                        <template v-slot:item2="{}">
                            <a class="dropdown-item" :href="`${store.errorDescriptionsDownloadUrl}xlsx`">Download as .xlsx</a>
                        </template>
                    </drop-down>
                </div>                    
            </template>
        </b-table-header>
        <b-table :items="dataItems"
            :columns="columns"
            :caption="'List of errors and warnings'"
            :value="options"
            @optionChange="handleOptionChange">
            <template v-slot:cell.status="{ item, column }">
                <button class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    @click.prevent="handleStatusClick(item)"
                    v-if="isStatusLinkEnabled(item)">{{ item.status }}</button>
                <span v-else>{{ item.status }}</span>
            </template>
            <template #cell.incidentNumber="{ item }">
                <a class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    :href="getEditorPageName(getValueFromSource(item, 'incidentNumber'), getValueFromSource(item, 'ori'))"
                    v-if="enableLinkToEditor">{{ item["incidentNumber"] }}</a>
                <span v-if="!enableLinkToEditor">{{ item["incidentNumber"] }}</span>
            </template>
            <template #cell.errorCode="{ item }">
                <button type="button"
                    class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    v-on:click.prevent="handleErrorCodeClick(item)">{{ item["errorCode"] }}</button>
            </template>
        </b-table>
        <b-table-header :value="options"
            @optionChange="handleOptionChange"
            header-slot-class="b-header-slot w-100"
            :filters="filterOptions.filters"
            :current-item-count="numberOfReturnedItems">
        </b-table-header>
    </div>
</template>
<script lang="ts">
import { defineComponent, watch, ref } from "vue";
import axios, { AxiosResponse } from "axios";
import BTable from "./BTable/BTable.vue";
import BTableHeader from "./BTable/BTableHeader.vue";
import { BTableColumn, BTableOptions } from "./BTable/BTable.types";
import { serializeFilters, serializeSortBy } from "./BTable/HelperFunctions";
import LoadingSpinner from "./Misc/LoadingSpinner.vue";
import {
    textOperations,
    listOperations,
    dateTimeOperations,
    describeFilters,
    BFieldSelection,
    BFilterOptions,
    BFilter,
    BFilterableField
} from "./BFilterEditor/BFilterEditor.types";
import BFilterModal from "./BFilterEditor/BFilterModal.vue";
import { debounce } from "vue-debounce";
import InformationModal from "./Misc/InformationModal.vue";
import { UploadType } from "@/types";
import ErrorsAndWarningsUpdate from "./ErrorsAndWarningsUpdate.vue";
import { getHtmlContent } from '@/utils';
import { Store } from "pinia";
import { useCommunityPolicingStore } from "@/stores/communityPolicingStore";
import { useContactsStore } from "@/stores/contactsStore";
import { useNibrsStore } from "@/stores/nibrsStore";
import { useUofStore } from "@/stores/uofStore";
import { useSummaryStore } from "@/stores/summaryStore";
import { useNarrativeStore } from "@/stores/narrativeStore";
import { usePoliceEmployeeStore } from "@/stores/policeEmployeeStore";
import { usePopulationStore } from "@/stores/populationStore";
import DropDown from "@/components/Misc/DropDown.vue";

export default defineComponent({
    components: {
        BTable,
        BTableHeader,
        LoadingSpinner,
        BFilterModal,
        InformationModal,
        DropDown
    },
    props: {
        url: String,
        title: String,
        dataset: String,
        onlyHasOneOri: Boolean,
        enableLinkToEditor: Boolean,
        hasClearError: Boolean,
        hasClearWarning: Boolean,
        editorUrl: String,
        noTotal:Boolean
    },
    computed: {
        filterableFields(): BFilterableField[] {
            return this.store.getFilterableFieldsForErrorsAndWarnings(this.onlyHasOneOri, this.showAdditionalColumns, false) as BFilterableField[];
        },
        columns(): BTableColumn[] {
            return this.store.getErrorsAndWarningsColumns(this.onlyHasOneOri, this.showAdditionalColumns, false);
        },
        filtersDescription(): string {
            if (this.filterOptions == null) {
                return "";
            }
            return describeFilters(
                this.filterOptions.filters,
                this.filterableFields
            );
        },
        canApplyFilters(): boolean {
            return (
                this.filterOptions != null &&
                this.filterOptions.filters.every(
                    (it: any) => it.property && it.operation && it.value
                )
            );
        }     
    },
    setup(props) {
        const dataItems = ref([] as any[]);
        var numberOfReturnedItems = ref(0);
        const agency = ref("");
        var agencies = ref([] as BFieldSelection[]);
        const incidentNumbers = ref([] as any[]);
        const isShowFilterEditor = ref(false);
        const total = ref(0);
        var showLoading = ref(false);
        var updateModalTitle = ref("");
        var showUpdateModal = ref(false);
        var selectedItem = ref({} as any);
        var store = {} as any;
    const uploadType: UploadType = UploadType[props.dataset ?? ""];

    switch (uploadType) {
        case UploadType.contacts:
            store = useContactsStore();
            break;
        case UploadType.communityPolicing:
            store = useCommunityPolicingStore();
            break;
        case UploadType.nibrs:
            store = useNibrsStore();
            break;
        case UploadType.summary:
            store = useSummaryStore();
            break;
        case UploadType.useOfForce:
            store = useUofStore();
            break;
        case UploadType.narrative:
            store = useNarrativeStore();
            break;
        case UploadType.policeEmployee:
            store = usePoliceEmployeeStore();
            break;
        case UploadType.population:
            store = usePopulationStore();
            break;
    }
        const tooltips = ref([] as any[]);

        //Saved table options or the default options
        let optionsJson = sessionStorage.getItem(`ew_${uploadType}_tableOptions`);
        
        var options = ref(optionsJson? JSON.parse(optionsJson) as BTableOptions : {
            totalItemCount: 0,
            itemsPerPage: 25,
            page: 1,
            sortBy: [],            
            pagingMode: (props.noTotal?"noTotal":"withTotal"),
            isLastPage:false
        } as BTableOptions);

        //filters
        let filtersJson = sessionStorage.getItem(`ew_${uploadType}_filterOptions`)
        var filterOptions = ref(filtersJson? JSON.parse(filtersJson) as BFilterOptions : {
            filters: [
                { property: "status", operation: "eq", value: "Unresolved" },
            ] as BFilter[],
        } as BFilterOptions);

        // Something that should be a computed property but isn't for reasons I don't remember
        const hasAppliedFilters = ref(filterOptions.value.filters.length > 0);

        const showAdditionalColumns = ref(false);

        const updateTableDataSource = debounce(
            (context: any) => context.getData(),
            "30000ms"
        );

        const showErrorDescriptionModal = ref(false);
        const selectedErrorDescription = ref("");
        const selectedErrorCode = ref("");
        const selectedErrorType = ref("");
        
        var selectedErrorBody = ref("");

        return {
            store,
            tooltips,
            options,
            filterOptions,
            hasAppliedFilters,
            showAdditionalColumns,
            dataItems,
            numberOfReturnedItems,
            agency,
            agencies,
            incidentNumbers,
            isShowFilterEditor,
            total,
            showLoading,
            updateTableDataSource,
            showErrorDescriptionModal,
            selectedErrorDescription,
            selectedErrorCode,
            selectedErrorType,
            showUpdateModal,
            updateModalTitle,
            selectedItem,
            selectedErrorBody,
            uploadType            
        };
    },
    methods: {
        getData: function () {
            if (this.isShowFilterEditor) {
                return;
            }

            this.showLoading = true;
            this.store.getErrorsAndWarnings(this.filterOptions.filters, this.options, this.store.getFilterableFieldsForErrorsAndWarnings(this.onlyHasOneOri,this.showAdditionalColumns, false), this.options.sortBy)
            .then((response:AxiosResponse) => {
                if(this.options.pagingMode == "noTotal" && response?.data?.data?.length > 0) {
                    //When were hiding the total text,first button, and last button then we return itemsPerPage + 1 in our get data call. 
                    //If the returned amount of rows is greater than itemsPerPage then enable the next button otherwise disable the next button to prevent an error
                    this.numberOfReturnedItems = response.data.data.length;
                    this.dataItems = response.data.data.slice(0,this.options.itemsPerPage);                    
                }
                else{
                    this.dataItems = response.data.data;
                }
                
                this.total = response.data.total;
                this.options.totalItemCount = this.total;
            })
            .catch((error:any) => {
                console.error(error?.response?.statusText);
            })
            .finally(() => {
                this.showLoading = false;
                this.updateTableDataSource(this);
            });
        },
        agencyFilterChange(agencyFilterValue) {
            this.agency = agencyFilterValue;
        },
        applyFilters(filterOptions) {
            this.filterOptions = filterOptions;
            this.isShowFilterEditor = false;
            this.hasAppliedFilters = this.filterOptions.filters.length > 0;

            if (this.hasAppliedFilters) {
                this.options.page = 1;
            }

            this.getData();
        },
        exportData: function (fileType: string) {
            let filters = [] as BFilter[];
            if (this.hasAppliedFilters) {
                filters = this.filterOptions.filters;
            }

            let filterText = serializeFilters(filters, this.filterableFields);
            let sortText = serializeSortBy(this.options.sortBy);

            var fullUrl = this.url;
            fullUrl += `export/${fileType}`;
            axios
                .get(fullUrl!, {
                    responseType: "arraybuffer",
                    params: { filter: filterText, sort: sortText },
                })
                .then(function (response) {
                    var blob = new Blob([response.data], {
                        type: response.headers["content-type"],
                    });
                    var link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = response.headers[
                        "content-disposition"
                    ].substring(
                        response.headers["content-disposition"].indexOf(
                            "filename="
                        ) + 9
                    );
                    link.click();
                });
        },
        handleStatusClick: function (item) {
            if (item != null) {
                var idNumber = (this.dataset == 'communityPolicing') ? item['stopNumber'] : item['incidentNumber'];                
                
                this.updateModalTitle = `Errors and Warnings Update`; // The following can used instead for a more descriptive header: ${item.agency} - ${idNumber} - ${item['error-code']}
                this.selectedItem = item;
                this.showUpdateModal = true;
            }
        },
        resetFilters: function () {
            this.options.page = 1;

            if (this.filterOptions != null) {
                this.filterOptions.filters = [];

                this.getData();
            }

            this.isShowFilterEditor = false;
            this.hasAppliedFilters = false;
        },
        showFilterEditor: function (event) {
            event.preventDefault();

            this.isShowFilterEditor = true;
        },
        handleDismiss: function () {
            this.isShowFilterEditor = false;
        },
        handleOptionChange: function (options) {
            if (options) {
                this.options = options;
            }
        },
        getValueFromSource(source, target): string {
            if (source && target) {
                let value = source[target];

                if (value) {
                    return encodeURIComponent(value);
                }
            }
            return "";
        },
        handleErrorDescriptionModalDismiss() {
            this.showErrorDescriptionModal = false;
        },
        handleUpdateModalDismiss() {
            this.showUpdateModal = false;
            this.getData();
        },
        handleErrorCodeClick: function (item: any) {
            let errorCode = item["errorCode"];
            this.selectedErrorCode = "";
            this.selectedErrorDescription = "";
            this.selectedErrorBody = "No information available";

            if (
                errorCode != undefined &&
                this.store.errorDescriptions &&
                this.store.errorDescriptions.value
            ) {
                this.selectedErrorCode = errorCode;
                this.store.errorDescriptions.value.forEach((f) => {
                    if (f.code == errorCode) {
                        this.selectedErrorType = item["errorType"];
                        this.selectedErrorDescription = f.help;

                        var descriptionText = "";

                        getHtmlContent(`help.aspx?e=${this.selectedErrorCode}`)
                        .then(data => descriptionText = data)
                        .catch(err => descriptionText = '')
                        .finally(() => {
                            if (descriptionText != '') {
                                this.selectedErrorBody = descriptionText;
                            }
                            else if (this.selectedErrorDescription != '') {
                                this.selectedErrorBody = `${this.selectedErrorDescription}`;
                            }
                            else{
                                this.selectedErrorBody= 'No information available';
                            }
                            this.showErrorDescriptionModal = true;
                            return;
                        });
                    }
                });
                this.showErrorDescriptionModal = true;
            }
        },
        isStatusLinkEnabled: function (item: any) {
            return (
                (item["errorType"] == "Warning" && this.hasClearWarning) ||
                (item["errorType"] == "Error" && this.hasClearError)
            );
        },
        getEditorPageName(incidentNumber: string, ori: string): string {
            
            if(this.editorUrl != undefined){
                var editorUrl = new URL(this.editorUrl)!;

                editorUrl.searchParams.append('incident', incidentNumber);
                editorUrl.searchParams.append('ori', ori);                
                editorUrl.searchParams.append('returnurl', window.location.href);
                
                return editorUrl.href;
            }

            return "";
        }        
    },
    watch: {
        options: {
            handler(newValue) {
                sessionStorage.setItem(`ew_${this.uploadType}_tableOptions`, JSON.stringify(this.options));
                this.getData();
            },
            deep: true,
        },
        filterOptions: {
            handler(newValue) {
                sessionStorage.setItem(`ew_${this.uploadType}_filterOptions`, JSON.stringify(this.filterOptions));
                this.getData();
            },
            deep: true,
        },
    },
    mounted() {
        this.getData();
    },
});
</script>
